/**
 * Canonical value sets for the internal Voice Admin page.
 *
 * Values MUST match the backend `VoiceMeta` model exactly. Display labels are
 * i18n keys (under `resources.admin.voices.options.*`) that the consuming
 * components translate via `useTranslation`.
 */
export interface VoiceOption {
  /** Raw value sent to / received from the backend. */
  value: string
  /** i18n key resolving to a human-friendly label. */
  labelKey: string
}

export const GENDER_OPTIONS: VoiceOption[] = [
  { value: 'male', labelKey: 'resources.admin.voices.options.gender.male' },
  { value: 'female', labelKey: 'resources.admin.voices.options.gender.female' },
  { value: 'neutral', labelKey: 'resources.admin.voices.options.gender.neutral' },
]

// Note: backend value is `youngadult` (no underscore).
export const AGE_GROUP_OPTIONS: VoiceOption[] = [
  { value: 'adult', labelKey: 'resources.admin.voices.options.age_group.adult' },
  { value: 'youngadult', labelKey: 'resources.admin.voices.options.age_group.youngadult' },
]

export const TONE_OPTIONS: VoiceOption[] = [
  { value: 'warm_friendly', labelKey: 'resources.admin.voices.options.tone.warm_friendly' },
  { value: 'simple_direct', labelKey: 'resources.admin.voices.options.tone.simple_direct' },
  { value: 'formal', labelKey: 'resources.admin.voices.options.tone.formal' },
]

export const PRICING_TIER_OPTIONS: VoiceOption[] = [
  { value: 'included', labelKey: 'resources.admin.voices.options.pricing.included' },
  { value: 'premium', labelKey: 'resources.admin.voices.options.pricing.premium' },
]

/**
 * Provider types whose synthesizer runs in the call pipeline; only their voices
 * can be a language default (`meta.default_for`), mirroring the backend guard.
 */
export const LANGUAGE_DEFAULT_PROVIDER_TYPES = ['azure', 'elevenlabs', 'mistral']

// Providers whose synthesizer reads `config.rate`; the rate control is hidden for the others.
export const RATE_PROVIDER_TYPES = ['azure', 'elevenlabs']

export interface ModelSettings {
  /** Models the call pipeline supports. Model names are not translated. */
  options: { value: string; label: string }[]
  /** i18n key for the helper text under the select. */
  helperKey: string
}

// Providers whose synthesizer reads `config.model_id`, with the models offered per provider;
// the model select is hidden for the others.
export const MODEL_SETTINGS_BY_PROVIDER_TYPE: Record<string, ModelSettings> = {
  elevenlabs: {
    options: [
      { value: 'eleven_flash_v2_5', label: 'Flash v2.5' },
      { value: 'eleven_v4_turbo', label: 'v4 Turbo' },
    ],
    helperKey: 'resources.admin.voices.helpers.model',
  },
  mistral: {
    options: [
      { value: 'voxtral-mini-tts-2603', label: 'Voxtral TTS 2' },
      { value: 'voxtral-mini-tts-3-0', label: 'Voxtral TTS 3' },
    ],
    helperKey: 'resources.admin.voices.helpers.model_mistral',
  },
}
